<script lang="ts">
    import ToggleSwitch from '../components/ToggleSwitch.svelte';
    import { useForm, page } from '@inertiajs/svelte';

    const userData = $derived($page?.props?.user as { name: string; email: string; email_reminders: boolean | null; leaderboard_opt_in: boolean | null; has_gdpr_consent: boolean } | undefined);
    const returning: boolean = $derived(!!$page?.props?.returning);
    const errors = $derived($page?.props?.errors ?? {});

    const form = useForm({
        name: '',
        email: '',
        email_reminders: true,
        leaderboard_opt_in: true,
        gdpr_consent: false,
    });

    let initialized = $state(false);
    $effect(() => {
        if (userData && !initialized) {
            $form.name = userData.name === 'User' ? '' : userData.name;
            $form.email = userData.email ?? '';
            $form.email_reminders = userData.email_reminders ?? false;
            $form.leaderboard_opt_in = userData.leaderboard_opt_in ?? true;
            $form.gdpr_consent = userData.has_gdpr_consent;
            initialized = true;
        }
    });

    function submit(e: SubmitEvent) {
        e.preventDefault();
        $form.post('/onboarding');
    }
</script>

<svelte:head>
    <title>Welcome — Case of the Day</title>
</svelte:head>

<div class="min-h-screen flex items-center justify-center bg-gray-950">
    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 w-full max-w-lg">
        <h1 class="text-2xl font-bold text-gray-100 mb-2">
            {returning ? 'Welcome Back' : 'Welcome to Case of the Day'}
        </h1>
        {#if returning}
            <p class="text-sm text-gray-400 mb-2">It's been a while since your last login!</p>
        {/if}
        <p class="text-sm text-gray-400 mb-6">
            {returning
                ? 'Please review your information and preferences before continuing.'
                : 'Please confirm your information and set your preferences before continuing.'}
        </p>

        <form onsubmit={submit} class="space-y-5">
            <div>
                <label for="name" class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                <input
                    bind:value={$form.name}
                    id="name"
                    type="text"
                    autocomplete="name"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.name ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.name}<p class="text-red-500 text-sm mt-1">{errors.name}</p>{/if}
            </div>

            <div>
                <label for="email" class="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                    bind:value={$form.email}
                    id="email"
                    type="email"
                    autocomplete="email"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.email ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.email}<p class="text-red-500 text-sm mt-1">{errors.email}</p>{/if}
            </div>

            <div class="space-y-3">
                <h2 class="text-sm font-medium text-gray-300">Preferences</h2>
                <div class="flex items-center justify-between gap-4">
                    <div>
                        <p class="text-sm text-gray-300">Email reminders</p>
                        <p class="text-xs text-gray-500">Receive daily email reminders about new cases</p>
                    </div>
                    <ToggleSwitch bind:checked={$form.email_reminders} />
                </div>
                <div class="flex items-center justify-between gap-4">
                    <div>
                        <p class="text-sm text-gray-300">Show name on leaderboard</p>
                        <p class="text-xs text-gray-500">Display your name on the leaderboard instead of "Anonymous"</p>
                    </div>
                    <ToggleSwitch bind:checked={$form.leaderboard_opt_in} />
                </div>
            </div>

            {#if !userData?.has_gdpr_consent}
                <div>
                    <div class="flex items-start gap-2">
                        <input
                            bind:checked={$form.gdpr_consent}
                            type="checkbox"
                            id="gdpr"
                            class="mt-1 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500 bg-gray-800"
                        />
                        <label for="gdpr" class="text-sm {errors.gdpr_consent ? 'text-red-400' : 'text-gray-400'}">
                            I consent to the storage and processing of my personal data and understand my data will be processed in the US in accordance with the <a href="/privacy" target="_blank" class="text-cyan-400 underline hover:text-cyan-300">privacy policy</a>. // eslint-disable
                        </label>
                    </div>
                    {#if errors.gdpr_consent}<p class="text-red-500 text-sm mt-1">{errors.gdpr_consent}</p>{/if}
                </div>
            {/if}

            <button
                type="submit"
                disabled={$form.processing}
                class="w-full bg-cyan-600 text-white py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
            >
                Continue
            </button>
        </form>
    </div>
</div>
